import type { TelemetrySummary, TelemetryVital } from '@/apis/telemetry';
import { ChunkStatus, type ChunkMeasureStatus } from '../queries';
import { ChunkName, type SeriesChunk } from './chunks';
import type { Extent } from './extent';

/** Which span of the shipment's readings the charts show. */
export enum DataRange {
  Operational = 'operational',
  Full = 'full',
}

/** What the charts draw for a range: the x window, the operational bounds (marked only in the full range), and pickup and delivery when known. */
export interface RangeView {
  window: Extent;
  operational: Extent;
  marked: boolean;
  pickedUp: number | null;
  delivered: number | null;
}

const isTail = (chunk: SeriesChunk): boolean =>
  chunk.name !== ChunkName.Operational && chunk.to > chunk.from;

/**
 * The view for a range: the operational window alone, or first to last reading with the operational bounds
 * marked. With no readings outside the trip, the full range is the operational window, still marked.
 */
export const rangeView = (
  range: DataRange,
  chunks: readonly SeriesChunk[],
  anchors: Pick<TelemetrySummary['anchors'], 'picked_up' | 'delivered'>
): RangeView | null => {
  const operational = chunks.find(
    (chunk) => chunk.name === ChunkName.Operational
  );
  if (!operational) return null;
  const bounds: Extent = [operational.from, operational.to];
  const known = { pickedUp: anchors.picked_up, delivered: anchors.delivered };
  if (range === DataRange.Operational) {
    return { window: bounds, operational: bounds, marked: false, ...known };
  }
  return {
    window: [chunks[0].from, chunks[chunks.length - 1].to],
    operational: bounds,
    marked: true,
    ...known,
  };
};

/** The before-pickup and after-delivery spans whose readings for this chart failed or were too large to load. */
export const notLoadedSpans = (
  chunks: readonly SeriesChunk[],
  status: ChunkMeasureStatus,
  measure: TelemetryVital
): Extent[] =>
  chunks
    .filter((chunk) => {
      const state = status[chunk.name]?.[measure];
      return (
        isTail(chunk) &&
        (state === ChunkStatus.TooLarge || state === ChunkStatus.Failed)
      );
    })
    .map((chunk): Extent => [chunk.from, chunk.to]);
